import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, ChevronDown, ImagePlus, Mic, Plus, Send, Square, X } from 'lucide-react';
import {
  fetchMessages,
  setCoordinator,
  startSession,
  stopRoom,
  fetchRooms,
  sendAttachment,
  sendMessage,
  sendSnapshot,
  type StolMessage,
  type StolRole,
  type StolRoom,
  type StolRoomUsage,
} from './api';
import { collectPassport } from './passport';
import { captureVisibleScreen } from './snapshot';
import { useStolChatStore, useStolStore } from './store';

/**
 * Панель Стола: выдвижная справа (~⅓), живёт над маршрутами — переходы по
 * админке разговор не обрывают. Выход сессии Code — читабельные сообщения
 * из штатного журнала разговора (мост stol:bridge), работа инструментов
 * свёрнута в одну строку. Живое — опросом (2 с), не потоком.
 */

const POLL_MS = 2000;
const ROOMS_POLL_MS = 5000;
const ROLES: StolRole[] = ['shtab', 'koordinator', 'ispolnitel', 'svobodnaya'];
type Words = (key: string) => string;

/** «s965 · Codex · Координатор» — who is in the session, as the owner chose it. */
function roomLabel(r: StolRoom, w: Words): string {
  const parts = [r.name, w(`provider.${r.provider ?? 'claude'}`)];
  if (r.role) parts.push(w(`role.${r.role}`));
  else if (r.model) parts.push(r.model);
  return parts.join(' · ');
}

function fmtTokens(n: number | undefined): string {
  if (n === undefined || n === null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} млн`;
  if (n >= 1_000) return `${Math.round(n / 1_000)} тыс`;
  return String(n);
}

/** Счёт за разговор: сколько сессия реально наговорила (вход + выход, без кэш-чтения). */
function conversationTokens(u: StolRoomUsage | null | undefined): number | undefined {
  if (!u) return undefined;
  return (u.input_tokens ?? 0) + (u.output_tokens ?? 0) + (u.cache_creation_input_tokens ?? 0);
}

/** «использовано ~N% · осталось M» — язык окна подписки, без денежных слов. */
function windowUsedLabel(u: StolRoomUsage | null | undefined): string {
  const left = u?.tokens_left;
  const base = u?.tokens_window;
  if (left === undefined || left === null) return '—';
  if (base && base >= left && base > 0) {
    const pct = Math.round(((base - left) / base) * 100);
    return `использовано ~${pct}% · осталось ${fmtTokens(left)}`;
  }
  return `осталось ${fmtTokens(left)}`;
}

export function StolPanel() {
  const { roomId, setRoom, setOpen, draft, setDraft } = useStolStore();
  const { byRoom, usageByRoom, appendMessages, setUsage } = useStolChatStore();
  const queryClient = useQueryClient();

  const [creating, setCreating] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const roomsQuery = useQuery({
    queryKey: ['stol-rooms'],
    queryFn: fetchRooms,
    refetchInterval: ROOMS_POLL_MS,
  });
  const rooms = useMemo(() => roomsQuery.data?.rooms ?? [], [roomsQuery.data]);
  const words = roomsQuery.data?.words ?? {};
  const w: Words = (key) => words[key] ?? key;
  const coordinatorName = roomsQuery.data?.coordinator ?? null;
  const [actionBusy, setActionBusy] = useState(false);
  const roomAction = async (fn: () => Promise<void>) => {
    setActionBusy(true);
    try {
      await fn();
      await queryClient.invalidateQueries({ queryKey: ['stol-rooms'] });
    } catch {
      setSendError(w('start_failed'));
    } finally {
      setActionBusy(false);
    }
  };
  const activeRoom = rooms.find((r) => r.id === roomId) ?? null;

  // Комнаты может не быть в списке ДВЕ причины: только что создана (список ещё
  // не обновился) или погасла (перезагрузка сервера). Выбор не сбрасываем сами —
  // гонка со свежесозданной комнатой обнуляла выбор; погасшую показываем честно.
  const roomGone =
    roomId !== null && roomsQuery.data !== undefined && !rooms.some((r) => r.id === roomId);

  const messages = roomId !== null ? (byRoom[roomId] ?? []) : [];
  const usage = roomId !== null ? usageByRoom[roomId] : null;
  const bridgeAlive = roomsQuery.data?.bridge_alive ?? true;

  // Опрос ленты активной комнаты: только пока панель открыта (панель
  // размонтируется при закрытии — опрос гаснет сам).
  useEffect(() => {
    if (roomId === null) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;

    const tick = async () => {
      try {
        const lastId = useStolChatStore.getState().lastId(roomId);
        const resp = await fetchMessages(roomId, lastId);
        if (stopped) return;
        appendMessages(roomId, resp.messages);
        setUsage(roomId, resp.room.usage);
      } catch {
        // Сеть мигнула — следующий тик попробует снова.
      }
      if (!stopped) timer = setTimeout(tick, POLL_MS);
    };
    tick();

    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [roomId, appendMessages, setUsage]);

  // Автоскролл вниз на новые сообщения.
  const lastMessageId = messages.length > 0 ? messages[messages.length - 1].id : 0;
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [lastMessageId, roomId]);

  const doSend = useCallback(async () => {
    const text = draft.trim();
    if (roomId === null || text === '') return;
    setSendError(null);
    try {
      const message = await sendMessage(roomId, text);
      appendMessages(roomId, [message]);
      setDraft('');
    } catch {
      setSendError('Сообщение не ушло — попробуй ещё раз.');
    }
  }, [draft, roomId, appendMessages, setDraft]);

  // «Показать экран»: ТОЛЬКО по кнопке (ТЗ §4.4) — снимок видимой области +
  // адрес + заголовок; на паспортизированном экране (Компас) + JSON состояния.
  const [snapBusy, setSnapBusy] = useState(false);
  const doSnapshot = useCallback(async () => {
    if (roomId === null || snapBusy) return;
    setSendError(null);
    setSnapBusy(true);
    try {
      const blob = await captureVisibleScreen();
      if (!blob) {
        setSendError('Снимок не собрался — экран не найден.');
        return;
      }
      const passport = collectPassport();
      const message = await sendSnapshot(roomId, {
        image: blob,
        url: window.location.pathname + window.location.search,
        title: document.title,
        passport: passport ? JSON.stringify(passport) : null,
      });
      appendMessages(roomId, [message]);
    } catch {
      setSendError('Снимок не ушёл — попробуй ещё раз.');
    } finally {
      setSnapBusy(false);
    }
  }, [roomId, snapBusy, appendMessages]);

  // `s979`: фото и голос — как у Ассистента в Telegram. Набранный текст уходит подписью.
  const photoInput = useRef<HTMLInputElement>(null);
  const [attachBusy, setAttachBusy] = useState(false);
  const doAttach = useCallback(async (kind: 'photo' | 'voice', file: Blob, name: string) => {
    if (roomId === null) return;
    setSendError(null);
    setAttachBusy(true);
    try {
      const message = await sendAttachment(roomId, kind, file, name, draft.trim());
      appendMessages(roomId, [message]);
      setDraft('');
    } catch {
      setSendError(kind === 'photo' ? 'Фото не ушло — попробуй ещё раз.' : 'Голосовое не ушло — попробуй ещё раз.');
    } finally {
      setAttachBusy(false);
    }
  }, [roomId, draft, appendMessages, setDraft]);

  const recorder = useRef<MediaRecorder | null>(null);
  const [recSeconds, setRecSeconds] = useState<number | null>(null);
  useEffect(() => {
    if (recSeconds === null) return;
    const t = setTimeout(() => {
      // не длиннее 10 минут — как у Ассистента
      if (recSeconds >= 600) recorder.current?.stop();
      else setRecSeconds((v) => (v === null ? null : v + 1));
    }, 1000);
    return () => clearTimeout(t);
  }, [recSeconds]);
  const toggleVoice = useCallback(async () => {
    if (recorder.current) {
      recorder.current.stop();
      return;
    }
    setSendError(null);
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setSendError('Нет доступа к микрофону — разреши его браузеру.');
      return;
    }
    const rec = new MediaRecorder(stream);
    const chunks: Blob[] = [];
    rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
    rec.onstop = () => {
      stream.getTracks().forEach((tr) => tr.stop());
      recorder.current = null;
      setRecSeconds(null);
      const type = rec.mimeType || 'audio/webm';
      const blob = new Blob(chunks, { type });
      if (blob.size > 0) void doAttach('voice', blob, type.includes('mp4') ? 'golos.m4a' : type.includes('ogg') ? 'golos.ogg' : 'golos.webm');
    };
    recorder.current = rec;
    rec.start();
    setRecSeconds(0);
  }, [doAttach]);

  return (
    <aside
      className="flex h-full shrink-0 flex-col border-l border-slate-200 bg-white text-[14px] text-[#1A1F2E]"
      style={{ width: 'clamp(340px, 33vw, 560px)' }}
      aria-label="Сессия — разговор с системой"
    >
      {/* Шапка панели: комната + создать + закрыть */}
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-slate-200 px-3">
        {/* Словарь владельца (21.08): поверхность для глаз — «Сессия»; «Стол» — кодовое имя. */}
        <span className="font-bold">Сессия</span>
        <div className="relative min-w-0 flex-1">
          <button
            type="button"
            onClick={() => setPickerOpen((v) => !v)}
            className="flex w-full items-center gap-1 truncate rounded-md border border-slate-200 px-2 py-1 text-left hover:bg-slate-50"
          >
            <span className="min-w-0 flex-1 truncate">
              {activeRoom ? roomLabel(activeRoom, w) : 'выбери сессию'}
            </span>
            <ChevronDown size={14} className="shrink-0 text-slate-400" />
          </button>
          {pickerOpen && (
            <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg">
              {rooms.length === 0 && (
                <div className="px-3 py-2 text-slate-500">Живых сессий нет — создай новую.</div>
              )}
              {rooms.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => {
                    setRoom(r.id);
                    setPickerOpen(false);
                  }}
                  className={`flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-slate-50 ${r.id === roomId ? 'font-semibold' : ''}`}
                >
                  <span className="min-w-0 flex-1 truncate">{roomLabel(r, w)}</span>
                  <span className="shrink-0 text-[12px] text-slate-400">
                    {r.status === 'creating' ? 'поднимается…' : r.status === 'failed' ? 'не поднялась' : r.name === coordinatorName ? w('coordinator') : ''}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          title="Создать сессию"
          aria-label="Создать сессию"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
        >
          <Plus size={18} />
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          title="Закрыть панель"
          aria-label="Закрыть панель"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
        >
          <X size={18} />
        </button>
      </div>

      {/* Индикатор расхода — словарь окна подписки (приёмка stl1: слово
          «кредиты» запрещено, оно денежное). Процент — от первого замера окна;
          времени обновления транскрипт не отдаёт — не выдумываем. */}
      <div className="flex shrink-0 items-center gap-3 border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-[13px] text-slate-600">
        <span>
          за разговор: <b>{fmtTokens(conversationTokens(usage))}</b>
        </span>
        <span>
          окно подписки: <b>{windowUsedLabel(usage)}</b>
        </span>
      </div>

      {activeRoom && activeRoom.status === 'alive' && (
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-slate-100 px-3 py-1.5 text-[13px]">
          {activeRoom.name === coordinatorName ? (
            <>
              <span className="text-slate-600">{w('coordinator_now')}</span>
              <button type="button" disabled={actionBusy} onClick={() => void roomAction(() => setCoordinator(activeRoom.id, false))}
                className="rounded-md px-2 py-1 text-slate-600 hover:bg-slate-100 disabled:opacity-40">{w('unset_coordinator')}</button>
            </>
          ) : (
            <button type="button" disabled={actionBusy} onClick={() => void roomAction(() => setCoordinator(activeRoom.id, true))}
              className="rounded-md px-2 py-1 text-slate-600 hover:bg-slate-100 disabled:opacity-40">{w('make_coordinator')}</button>
          )}
          <button type="button" disabled={actionBusy || activeRoom.stopping}
            onClick={() => { if (window.confirm(w('stop_confirm'))) void roomAction(() => stopRoom(activeRoom.id)); }}
            className="ml-auto rounded-md px-2 py-1 text-red-600 hover:bg-red-50 disabled:opacity-40">{w(activeRoom.stopping ? 'stopping' : 'stop')}</button>
        </div>
      )}

      {roomGone && (
        <div className="shrink-0 border-b border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800">
          Эта комната погасла (список живых её не видит) — выбери другую или создай новую.
        </div>
      )}
      {!bridgeAlive && (
        <div className="shrink-0 border-b border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800">
          Мост сессий не отвечает — сообщения не дойдут, пока он не поднимется.
        </div>
      )}
      {usage?.tokens_left !== undefined && usage.tokens_left < 500_000 && (
        <div className="shrink-0 border-b border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800">
          Окно подписки почти исчерпано ({fmtTokens(usage.tokens_left)} токенов) — сессия скоро упрётся в лимит.
        </div>
      )}

      {/* Лента */}
      <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
        {roomId === null ? (
          <div className="flex h-full items-center justify-center px-6 text-center text-slate-500">
            Выбери живую сессию или создай новую — и говори с системой прямо здесь.
          </div>
        ) : messages.length === 0 ? (
          <div className="flex h-full items-center justify-center px-6 text-center text-slate-500">
            Пока пусто. Напиши первое сообщение.
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {messages.map((m) => (
              <MessageRow key={m.id} m={m} />
            ))}
          </div>
        )}
      </div>

      {/* Композер */}
      <div className="shrink-0 border-t border-slate-200 p-2">
        {sendError && <div className="px-1 pb-1 text-[13px] text-red-600">{sendError}</div>}
        <div className="flex items-end gap-2">
          <button
            type="button"
            onClick={() => void doSnapshot()}
            disabled={roomId === null || snapBusy}
            title="Показать экран сессии"
            aria-label="Показать экран"
            data-testid="stol-snapshot"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-30"
          >
            <Camera size={16} className={snapBusy ? 'animate-pulse' : undefined} />
          </button>
          <button
            type="button"
            onClick={() => photoInput.current?.click()}
            disabled={roomId === null || attachBusy}
            title="Отправить фото"
            aria-label="Отправить фото"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-30"
          >
            <ImagePlus size={16} className={attachBusy ? 'animate-pulse' : undefined} />
          </button>
          <input
            ref={photoInput}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) void doAttach('photo', file, file.name || 'foto.jpg');
            }}
          />
          <button
            type="button"
            onClick={() => void toggleVoice()}
            disabled={roomId === null || (attachBusy && recSeconds === null)}
            title={recSeconds === null ? 'Записать голосовое' : 'Остановить и отправить'}
            aria-label={recSeconds === null ? 'Записать голосовое' : 'Остановить и отправить'}
            className={`flex h-10 shrink-0 items-center justify-center gap-1 rounded-md border px-2.5 disabled:opacity-30 ${
              recSeconds === null ? 'w-10 border-slate-200 text-slate-500 hover:bg-slate-50' : 'border-red-300 bg-red-50 text-red-700'
            }`}
          >
            {recSeconds === null ? <Mic size={16} /> : <><Square size={14} /><span className="tabular-nums">{Math.floor(recSeconds / 60)}:{String(recSeconds % 60).padStart(2, '0')}</span></>}
          </button>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void doSend();
              }
            }}
            rows={2}
            placeholder={roomId === null ? 'Сначала выбери сессию' : 'Напиши сессии… (Enter — отправить)'}
            disabled={roomId === null}
            className="min-h-[40px] flex-1 resize-none rounded-md border border-slate-200 px-2.5 py-2 outline-none focus:border-slate-400 disabled:bg-slate-50"
          />
          <button
            type="button"
            onClick={() => void doSend()}
            disabled={roomId === null || draft.trim() === ''}
            title="Отправить"
            aria-label="Отправить"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[#1A1F2E] text-white disabled:opacity-30"
          >
            <Send size={16} />
          </button>
        </div>
      </div>

      {creating && (
        <CreateRoomDialog
          w={w}
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setRoom(id);
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['stol-rooms'] });
          }}
        />
      )}
    </aside>
  );
}

function MessageRow({ m }: { m: StolMessage }) {
  if (m.kind === 'snapshot') {
    const title = (m.meta?.title as string | undefined) ?? '';
    const passported = m.meta?.passported === true;
    return (
      <div className="flex justify-end">
        <div className="max-w-[92%] rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[13px] text-slate-600">
          📸 Снимок экрана отправлен{title ? ` · ${title}` : ''}
          {!passported && <span className="ml-1 text-slate-400">(раздел не паспортизирован)</span>}
          <span className="ml-2 align-baseline text-[11px] opacity-60">
            {m.status === 'queued' ? '⏳' : m.status === 'sent' ? '→' : m.status === 'failed' ? '⚠' : '✓'}
          </span>
        </div>
      </div>
    );
  }

  if (m.kind === 'tool') {
    // Работа инструментов — свёрнутая серым в одну строку (runbook Этап 2).
    return (
      <div className="truncate pl-2 font-mono text-[12px] leading-5 text-slate-400" title={m.content}>
        ⚙ {m.content}
      </div>
    );
  }

  if (m.direction === 'system' || m.kind === 'notice') {
    return <div className="px-2 text-[13px] text-amber-700">{m.content}</div>;
  }

  const own = m.direction === 'owner';
  // `s979`: путь к файлу нужен сессии, на экране — что ушло.
  const attachment = m.meta?.attachment as 'photo' | 'voice' | undefined;
  const shown = attachment === 'photo'
    ? `📷 Фото${m.meta?.caption ? ` · ${String(m.meta.caption)}` : ''}`
    : attachment === 'voice'
      ? `🎤 ${m.meta?.transcript ? String(m.meta.transcript) : 'голосовое (не расшифровалось — сессия переслушает файл)'}`
      : m.content;
  return (
    <div className={`flex ${own ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[92%] whitespace-pre-wrap break-words rounded-lg px-3 py-2 leading-snug ${
          own ? 'bg-[#1A1F2E] text-white' : 'bg-slate-100 text-[#1A1F2E]'
        }`}
      >
        {shown}
        {own && (
          <span className="ml-2 align-baseline text-[11px] opacity-60">
            {m.status === 'queued' ? '⏳' : m.status === 'sent' ? '→' : m.status === 'failed' ? '⚠' : '✓'}
          </span>
        )}
      </div>
    </div>
  );
}

function CreateRoomDialog({
  w,
  onClose,
  onCreated,
}: {
  w: Words;
  onClose: () => void;
  onCreated: (roomId: number) => void;
}) {
  const [role, setRole] = useState<StolRole>('ispolnitel');
  const [provider, setProvider] = useState<StolRoom['provider']>('codex');
  const [task, setTask] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const room = await startSession(role, provider, task.trim());
      onCreated(room.id);
    } catch (e) {
      const msg =
        (e as { response?: { data?: { errors?: Record<string, string[]> } } }).response?.data
          ?.errors;
      setError(msg ? Object.values(msg).flat().join(' ') : w('start_failed'));
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4"
      onMouseDown={onClose}
      role="dialog"
      aria-label={w('new_session')}
    >
      <div
        className="max-h-[90vh] w-full max-w-[560px] overflow-y-auto rounded-lg bg-white p-4 shadow-xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="mb-3 text-[15px] font-bold">{w('new_session')}</div>
        <fieldset className="mb-3">
          <legend className="mb-1 block text-[13px] text-slate-500">{w('role')}</legend>
          <div className="grid gap-1.5">
            {ROLES.map((r) => (
              <label key={r} className={`flex cursor-pointer items-start gap-2 rounded-md border px-2.5 py-2 ${role === r ? 'border-slate-500 bg-slate-50' : 'border-slate-200'}`}>
                <input type="radio" name="stol-role" className="mt-1" checked={role === r} onChange={() => setRole(r)} />
                <span>
                  <span className="block font-medium">{w(`role.${r}`)}</span>
                  <span className="block text-[13px] text-slate-500">{w(`role.${r}.hint`)}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        <label className="mb-3 block">
          <span className="mb-1 block text-[13px] text-slate-500">{w('provider')}</span>
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value as StolRoom['provider'])}
            className="w-full rounded-md border border-slate-200 px-2 py-2 outline-none focus:border-slate-400"
          >
            <option value="codex">{w('provider.codex')}</option>
            <option value="claude">{w('provider.claude')}</option>
          </select>
          <span className="mt-1 block text-[12px] text-slate-500">{w('route_note')}</span>
        </label>
        <label className="mb-3 block">
          <span className="mb-1 block text-[13px] text-slate-500">{w('task')}</span>
          <textarea
            value={task}
            onChange={(e) => setTask(e.target.value)}
            rows={6}
            autoFocus
            placeholder={w('task_placeholder')}
            className="w-full resize-y rounded-md border border-slate-200 px-2.5 py-2 outline-none focus:border-slate-400"
          />
        </label>
        {error && <div className="mb-2 text-[13px] text-red-600">{error}</div>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-slate-600 hover:bg-slate-100"
          >
            {w('cancel')}
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={busy || (role !== 'svobodnaya' && task.trim() === '')}
            className="rounded-md bg-[#1A1F2E] px-3 py-1.5 text-white disabled:opacity-40"
          >
            {w(busy ? 'starting' : 'start')}
          </button>
        </div>
      </div>
    </div>
  );
}
